import { APPLICATION_DOCUMENT_GROUPS } from '../constants/applicationDocumentCatalog'

const STATUS_RANK = { success: 0, assigned: 1, missing: 2 }

const mapFlatDocument = doc => {
	const status = String(doc?.status || 'missing').toLowerCase()
	const uploaded =
		status === 'uploaded' || status === 'verified' || status === 'success'
	return {
		id: doc?.id || `m-${doc?.requirementId}`,
		requirementId: doc?.requirementId || doc?.requirement_id || doc?.id || '',
		title: doc?.name || doc?.title || 'Document',
		counselorStatus: uploaded ? 'success' : status === 'assigned' ? 'assigned' : 'missing',
		fileId: uploaded ? doc?.fileId || doc?.id || null : null,
		lastUpdated: doc?.uploadedAt || doc?.lastUpdated || '',
		lastNudgeAt: doc?.lastNudgeAt || null,
		pendingUpload: Boolean(doc?.pendingUpload),
		filename: doc?.filename || '',
	}
}

const normalizeApiItem = item => ({
	id: String(item?.id || ''),
	requirementId: String(item?.requirementId || item?.requirement_id || ''),
	title: String(item?.title || 'Document'),
	counselorStatus: String(item?.counselorStatus || 'missing'),
	fileId: item?.fileId || null,
	lastUpdated: item?.lastUpdated || '',
	lastNudgeAt: item?.lastNudgeAt || null,
	pendingUpload: Boolean(item?.pendingUpload),
	filename: item?.filename || '',
})

const pickPrimaryItem = items => {
	if (!items?.length) return null
	return [...items].sort(
		(a, b) =>
			(STATUS_RANK[a.counselorStatus] ?? 9) - (STATUS_RANK[b.counselorStatus] ?? 9),
	)[0]
}

// The API's groups, passed through.
//
// This used to project the response onto the hardcoded catalog, which silently
// dropped anything the catalog did not list - now that requirements are resolved
// per student from the courses they applied to, that would discard every
// course-specific document. It also collapsed multiple rows per requirement to
// one, hiding a second uploaded file; the backend already decides how many rows
// a requirement gets, so they are kept.
const fromApiGroups = apiGroups =>
	(apiGroups || [])
		.map(group => ({
			id: String(group?.id || ''),
			title: String(group?.title || ''),
			items: (group?.items || []).map(normalizeApiItem).filter(item => item.requirementId),
		}))
		.filter(group => group.items.length)

// Fallback only: a flat document list with no groups, from the fixture path.
// Here there is no server-side skeleton, so the hardcoded catalog supplies one.
const fromFlatDocuments = flatDocuments => {
	const byRequirement = new Map()
	for (const doc of flatDocuments || []) {
		const normalized = mapFlatDocument(doc)
		const rid = normalized.requirementId
		if (!rid) continue
		const bucket = byRequirement.get(rid) || []
		bucket.push(normalized)
		byRequirement.set(rid, bucket)
	}

	return APPLICATION_DOCUMENT_GROUPS.map(group => ({
		id: group.id,
		title: group.title,
		items: group.requirements.map(req => {
			const primary = pickPrimaryItem(byRequirement.get(req.id) || [])
			if (primary) {
				return { ...primary, title: req.title, requirementId: req.id }
			}
			return {
				id: `m-${req.id}`,
				requirementId: req.id,
				title: req.title,
				counselorStatus: 'missing',
				fileId: null,
				lastUpdated: '',
				lastNudgeAt: null,
				pendingUpload: false,
				filename: '',
			}
		}),
	}))
}

export const mergeApplicationDocumentGroups = (apiGroups, flatDocuments = []) => {
	const fromApi = fromApiGroups(apiGroups)
	if (fromApi.length) return fromApi
	return fromFlatDocuments(flatDocuments)
}
